/**
 * Clickstream tracker -- batches frontend events and flushes them
 * via sendBeacon to /api/v1/app-events.
 *
 * Pure TypeScript, no React dependency. Auth state is read from the
 * Zustand store at flush time via getState().
 */

import { apiUrl } from '@/lib/api-base';
import { useAuthStore } from '@/stores/authStore';
import { useAppModeStore } from '@/stores/appModeStore';

// --- Types ---

export interface DeviceInfo {
  userAgent: string;
  language: string;
  timezone: string;
  screenWidth: number;
  screenHeight: number;
  viewportWidth: number;
  viewportHeight: number;
}

export interface ReferralInfo {
  referrer: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmTerm: string;
  utmContent: string;
  inviteFrom: string;
  inviteTo: string;
  campaignTag: string;
}

export interface ClickstreamEvent {
  ts: string;
  type: string;
  page: string;
  target?: string;
  label?: string;
  meta?: Record<string, string>;
}

export interface ClickstreamBatch {
  v: 1;
  sid: string;
  uid: string | null;
  cid: string | null;
  device: DeviceInfo;
  referral: ReferralInfo;
  events: ClickstreamEvent[];
}

// --- Module state ---

const FLUSH_INTERVAL_MS = 10_000;
const BUFFER_CAP = 100;

// Persisted so the session id survives full-page reloads — including
// the Google OAuth redirect round-trip, which is otherwise unjoinable
// between pre- and post-auth clickstream events.
const SESSION_ID_STORAGE_KEY = 'rl_cs_sid';

function generateSessionId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `fallback-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function loadOrCreateSessionId(): string {
  if (typeof window === 'undefined') return generateSessionId();
  try {
    const existing = window.localStorage.getItem(SESSION_ID_STORAGE_KEY);
    if (existing) return existing;
    const fresh = generateSessionId();
    window.localStorage.setItem(SESSION_ID_STORAGE_KEY, fresh);
    return fresh;
  } catch {
    return generateSessionId();
  }
}

let sessionId = loadOrCreateSessionId();

/**
 * Adopt an externally-supplied session id (e.g. one round-tripped
 * through the OAuth `state` parameter as a belt-and-braces fallback
 * for when localStorage was cleared between redirects). Updates the
 * in-memory id AND localStorage so both this page's remaining events
 * and any subsequent loads converge on the same id.
 * No-op if the value is empty or identical to the current id.
 */
export function adoptSessionId(external: string): void {
  if (!external || external === sessionId) return;
  sessionId = external;
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(SESSION_ID_STORAGE_KEY, external);
    }
  } catch {
    // best-effort
  }
}

let buffer: ClickstreamEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;

function getDeviceInfo(): DeviceInfo {
  if (typeof window === 'undefined') {
    return {
      userAgent: '',
      language: '',
      timezone: '',
      screenWidth: 0,
      screenHeight: 0,
      viewportWidth: 0,
      viewportHeight: 0,
    };
  }
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
  };
}

const deviceInfo = getDeviceInfo();

// --- Referral helpers ---
// Links use base64url encoding for compact, URL-safe codes.
// Generate with: scripts/gen-referral-link.py

/** Decode a base64url string (no padding) to raw bytes. */
export function base64urlDecode(s: string): Uint8Array {
  const padded =
    s.replace(/-/g, '+').replace(/_/g, '/') + '=='.slice(0, (4 - (s.length % 4)) % 4);
  const binary = atob(padded);
  return Uint8Array.from(binary, (c) => c.charCodeAt(0));
}

/**
 * Decode an invite param (?i=CODE).
 * Binary format: byte 0 = 0x01 (has from) or 0x00 (no from),
 * bytes 1-4 = from_uid raw bytes (if 0x01), rest = "to" UTF-8 string.
 */
export function decodeInviteParam(raw: string): { from: string; to: string } {
  try {
    const bytes = base64urlDecode(raw);
    if (bytes.length === 0) return { from: '', to: '' };
    const hasFrom = bytes[0] === 0x01;
    if (hasFrom && bytes.length >= 5) {
      const fromUid = Array.from(bytes.slice(1, 5))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
      const to = bytes.length > 5 ? new TextDecoder().decode(bytes.slice(5)) : '';
      return { from: fromUid, to };
    }
    // No from — rest is "to"
    const to = bytes.length > 1 ? new TextDecoder().decode(bytes.slice(1)) : '';
    return { from: '', to };
  } catch {
    return { from: '', to: '' };
  }
}

/** Decode a campaign param (?c=CODE) — base64url of UTF-8 campaign name. */
export function decodeCampaignParam(raw: string): string {
  try {
    return new TextDecoder().decode(base64urlDecode(raw));
  } catch {
    return '';
  }
}

function getReferralInfo(): ReferralInfo {
  const empty: ReferralInfo = {
    referrer: '',
    utmSource: '',
    utmMedium: '',
    utmCampaign: '',
    utmTerm: '',
    utmContent: '',
    inviteFrom: '',
    inviteTo: '',
    campaignTag: '',
  };
  if (typeof window === 'undefined') return empty;
  try {
    const params = new URLSearchParams(window.location.search);
    const invite = params.get('i');
    const { from: inviteFrom, to: inviteTo } = invite ? decodeInviteParam(invite) : { from: '', to: '' };
    return {
      referrer: document.referrer || '',
      utmSource: params.get('utm_source') || '',
      utmMedium: params.get('utm_medium') || '',
      utmCampaign: params.get('utm_campaign') || '',
      utmTerm: params.get('utm_term') || '',
      utmContent: params.get('utm_content') || '',
      inviteFrom,
      inviteTo,
      campaignTag: params.has('c') ? decodeCampaignParam(params.get('c')!) : '',
    };
  } catch {
    return empty;
  }
}

const referralInfo = getReferralInfo();

// --- Public API ---

/**
 * Snapshot current-URL UTM params into an event's meta bag.
 * Used on page_viewed so downstream analytics can attribute the
 * landing page to its UTM campaign without rehydrating the
 * batch-level referral snapshot.
 */
function currentUtmMeta(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const out: Record<string, string> = {};
    for (const key of [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content',
    ]) {
      const v = params.get(key);
      if (v) out[key] = v;
    }
    return out;
  } catch {
    return {};
  }
}

/** Record a single event. Automatically fills ts and page. */
export function track(event: Omit<ClickstreamEvent, 'ts' | 'page'> & { page?: string }) {
  try {
    // page_viewed gets UTM params from the live URL merged into meta.
    // Existing meta keys win so callers can override per-event.
    let meta = event.meta;
    if (event.type === 'page_viewed') {
      const utm = currentUtmMeta();
      if (Object.keys(utm).length > 0) {
        meta = { ...utm, ...(meta ?? {}) };
      }
    }
    buffer.push({
      ts: new Date().toISOString(),
      page:
        event.page ??
        (typeof window !== 'undefined'
          ? window.location.pathname + window.location.search
          : ''),
      ...event,
      ...(meta !== undefined ? { meta } : {}),
    });
    if (buffer.length >= BUFFER_CAP) {
      flush();
    }
  } catch {
    // Never throw -- analytics must not break the app
  }
}

/** Flush buffered events via sendBeacon (or fetch fallback). */
export function flush() {
  if (buffer.length === 0) return;

  const events = buffer;
  buffer = [];

  try {
    const { aurigaUid, _conversationIds } = useAuthStore.getState();
    const conversationId = _conversationIds[useAppModeStore.getState().slug ?? ''] ?? null;

    const batch: ClickstreamBatch = {
      v: 1,
      sid: sessionId,
      uid: aurigaUid,
      cid: conversationId,
      device: deviceInfo,
      referral: referralInfo,
      events,
    };

    const body = JSON.stringify(batch);
    const url = apiUrl('/api/v1/app-events');

    // Use text/plain to avoid CORS preflight with sendBeacon
    const blob = new Blob([body], { type: 'text/plain' });

    let queued = false;
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      queued = navigator.sendBeacon(url, blob);
    }

    if (!queued && typeof fetch !== 'undefined') {
      // Fire-and-forget fetch fallback
      fetch(url, {
        method: 'POST',
        body,
        headers: { 'Content-Type': 'text/plain' },
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // Swallow -- best-effort delivery
  }
}

/** Start periodic auto-flush. */
export function startAutoFlush(intervalMs = FLUSH_INTERVAL_MS) {
  stopAutoFlush();
  flushTimer = setInterval(flush, intervalMs);
}

/** Stop periodic auto-flush. */
export function stopAutoFlush() {
  if (flushTimer !== null) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
}

// For testing
export function _getBuffer() {
  return buffer;
}
export function _getSessionId() {
  return sessionId;
}
export function _getReferralInfo() {
  return referralInfo;
}
export function _resetBuffer() {
  buffer = [];
}
